import { Copy, History, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { PaginatedResponse, PriceTable } from '@/api/commercial/price-tables';
import { PriceTableStatus, priceTableApi } from '@/api/commercial/price-tables';
import { PriceTableHistoryDialog } from '@/components/commercial/price-table/history-dialog';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

interface PriceTableTableProps {
  data: PaginatedResponse<PriceTable>;
  currentPage: number;
  onPageChange: (page: number) => void;
  canEdit?: boolean;
  canClone?: boolean;
  canViewHistory?: boolean;
}

const STATUS_LABEL: Record<PriceTableStatus, { label: string; color: string }> = {
  [PriceTableStatus.ACTIVE]: {
    label: 'Ativa',
    color: 'bg-green-100 text-green-800',
  },
  [PriceTableStatus.PAUSED]: {
    label: 'Em Pausa',
    color: 'bg-purple-100 text-purple-800',
  },
  [PriceTableStatus.DRAFT]: {
    label: 'Rascunho',
    color: 'bg-blue-100 text-blue-800',
  },
  [PriceTableStatus.AWAITING_PUBLICATION]: {
    label: 'Aguardando Publicação',
    color: 'bg-yellow-100 text-yellow-800',
  },
  [PriceTableStatus.INACTIVE]: {
    label: 'Inativa',
    color: 'bg-gray-200 text-gray-800',
  },
  [PriceTableStatus.CANCELLED]: {
    label: 'Cancelada',
    color: 'bg-red-100 text-red-800',
  },
};

export function PriceTableTable({
  data,
  currentPage,
  onPageChange,
  canEdit = true,
  canClone = true,
  canViewHistory = true,
}: PriceTableTableProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [historyPriceTable, setHistoryPriceTable] = useState<PriceTable | null>(null);

  const formatDateTime = (value?: string | Date): string => {
    if (!value) return '-';
    const date = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const resolveStatus = (status?: PriceTableStatus | string) => {
    if (!status) {
      return { label: '-', color: 'bg-gray-100 text-gray-800' };
    }

    if (typeof status === 'string' && status in STATUS_LABEL) {
      return STATUS_LABEL[status as PriceTableStatus];
    }

    return (
      STATUS_LABEL[status as PriceTableStatus] ?? {
        label: status,
        color: 'bg-gray-100 text-gray-800',
      }
    );
  };

  const handleClone = async (priceTable: PriceTable) => {
    try {
      setCloningId(priceTable.id);
      const result = await priceTableApi.clone(priceTable.id);

      if (result?.success && result.id) {
        toast({
          variant: 'success',
          title: 'Tabela clonada!',
          description: 'Rascunho criado. Você pode continuar a edição.',
        });
        navigate(`/commercial/price-table/form/edit/${result.id}`);
        return;
      }

      toast({
        title: 'Não foi possível clonar a tabela.',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      });
    } catch {
      toast({
        title: 'Erro ao clonar tabela.',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      });
    } finally {
      setCloningId(null);
    }
  };

  const handleViewHistory = (priceTable: PriceTable) => {
    setHistoryPriceTable(priceTable);
  };

  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>NOME DA TABELA</TableHead>
              <TableHead>DATA DE CADASTRO</TableHead>
              <TableHead>DATA INICIAL</TableHead>
              <TableHead>DATA FINAL</TableHead>
              <TableHead>TIPO</TableHead>
              <TableHead>STATUS</TableHead>
              <TableHead className="text-right">AÇÃO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  Nenhuma tabela de preços encontrada
                </TableCell>
              </TableRow>
            ) : (
              docs.map((priceTable) => {
                const status = resolveStatus(priceTable.status);
                const endDate = priceTable.inactivated_at || priceTable.endDateTime;
                const isCloning = cloningId === priceTable.id;

                return (
                  <TableRow key={priceTable.id}>
                    <TableCell>
                      <span className="font-medium">{priceTable.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(priceTable.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(priceTable.startDateTime)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDateTime(endDate)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {priceTable.isTemporary ? 'Provisória' : 'Normal'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canViewHistory && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            title="Histórico"
                            onClick={() => handleViewHistory(priceTable)}
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        )}
                        {canClone && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            title="Clonar tabela"
                            onClick={() => handleClone(priceTable)}
                            disabled={isCloning}
                          >
                            {isCloning ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        {canEdit && (
                          <Link to={`/commercial/price-table/form/edit/${priceTable.id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                              title="Editar"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />

      {historyPriceTable && (
        <PriceTableHistoryDialog
          open={!!historyPriceTable}
          onOpenChange={(open) => {
            if (!open) setHistoryPriceTable(null);
          }}
          priceTableId={historyPriceTable.id}
          priceTableName={historyPriceTable.name}
        />
      )}
    </div>
  );
}
